export const addItemsToCart = (
  items: Ri2PartCartItem[],
  caseId: string,
): void => window!.ri2!.addItemsToCart(items, caseId)

/**
 * Navigate using the host's client-side router when available
 * (smooth SPA transition, no full page reload). Falls back to window.open
 * when running standalone or in the admin demo.
 *
 * Strips the origin from absolute URLs so router.push() receives a relative
 * path and works on localhost, QA, and production alike.
 */
export const hostNavigate = (url: string): void => {
  let target = url
  try {
    const parsed = new URL(url)
    if (parsed.origin === window.location.origin) {
      target = parsed.pathname + parsed.search + parsed.hash
    }
  } catch {
    // relative path or malformed URL -- use as-is
  }

  if (window.ri2?.navigateTo) {
    window.ri2.navigateTo(target)
  } else {
    window.open(target, "_blank", "noopener,noreferrer")
  }
}

/**
 * Navigate to a part detail page.
 *
 * Prefers window.ri2.getPartDetailUrl so the host controls both the URL
 * format and the part IDs (the host's catalog is authoritative; rc-db IDs
 * may differ on QA/localhost). Falls back to part.purchaseUrl if the host
 * callback is unavailable (standalone / admin demo).
 */
export const navigateToPart = (part: {
  id: number
  name: string | null
  manufacturerPartNumber: string
  purchaseUrl: string | null
}): void => {
  const url =
    window.ri2?.getPartDetailUrl?.({
      id: part.id,
      name: part.name ?? "",
      oemNumber: part.manufacturerPartNumber,
    }) ?? part.purchaseUrl

  if (url) hostNavigate(url)
}

type PendingHostPartPriceRequest = {
  item: Ri2PartPriceRequestItem
  resolvers: Array<(value: HostPartPriceData) => void>
}

const HOST_PRICE_BATCH_MS = 0

type HostPartPriceData = {
  price: number | null | undefined
  stockStatus: Ri2PartStockStatus
}

const emptyHostPartPriceData: HostPartPriceData = {
  price: undefined,
  stockStatus: undefined,
}

const hostPriceCache = new Map<string, HostPartPriceData>()
const inFlightPriceRequests = new Map<string, Promise<HostPartPriceData>>()
const queuedPriceRequests = new Map<string, PendingHostPartPriceRequest>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

const normalizePriceKey = (oemNumber: string): string =>
  oemNumber.trim().toUpperCase()

const toHostPartPriceData = (
  value: Ri2PartPriceResponseValue,
): HostPartPriceData => {
  if (typeof value === "number" || value == null) {
    return {
      price: value,
      stockStatus: undefined,
    }
  }

  return {
    price: value.price,
    stockStatus: value.inventory_status ?? value.stock_status,
  }
}

const ensureFlushScheduled = (): void => {
  if (flushTimer) return

  flushTimer = setTimeout(() => {
    flushTimer = null
    void flushQueuedHostPriceRequests()
  }, HOST_PRICE_BATCH_MS)
}

const flushQueuedHostPriceRequests = async (): Promise<void> => {
  if (!queuedPriceRequests.size) return

  const queuedEntries = [...queuedPriceRequests.entries()]
  queuedPriceRequests.clear()

  const getPartPrices = window.ri2?.getPartPrices

  // No host function means no price source of truth is available.
  if (!getPartPrices) {
    for (const [key, request] of queuedEntries) {
      request.resolvers.forEach((resolve) => resolve(emptyHostPartPriceData))
      inFlightPriceRequests.delete(key)
    }
    return
  }

  try {
    const items = queuedEntries.map(([, request]) => request.item)
    const partDataByOemNumber = await getPartPrices(items)

    for (const [key, request] of queuedEntries) {
      const partData = toHostPartPriceData(partDataByOemNumber?.[key])
      hostPriceCache.set(key, partData)
      request.resolvers.forEach((resolve) => resolve(partData))
      inFlightPriceRequests.delete(key)
    }
  } catch {
    for (const [key, request] of queuedEntries) {
      request.resolvers.forEach((resolve) => resolve(emptyHostPartPriceData))
      inFlightPriceRequests.delete(key)
    }
  }
}

export const toPartPriceRequestItem = (
  item: Pick<Ri2PartCartItem, "oem_number"> & {
    manufacturer_name: string
  },
): Ri2PartPriceRequestItem => ({
  oem_number: normalizePriceKey(item.oem_number),
  manufacturer_name: item.manufacturer_name,
})

export const getHostPartPriceAndStockStatus = async (
  item: Ri2PartPriceRequestItem,
): Promise<HostPartPriceData> => {
  const key = normalizePriceKey(item.oem_number)
  if (!key) return emptyHostPartPriceData

  if (hostPriceCache.has(key)) {
    return hostPriceCache.get(key) ?? emptyHostPartPriceData
  }

  const inFlight = inFlightPriceRequests.get(key)
  if (inFlight) {
    return inFlight
  }

  const pendingPromise = new Promise<HostPartPriceData>((resolve) => {
    const queued = queuedPriceRequests.get(key)
    if (queued) {
      queued.resolvers.push(resolve)
    } else {
      queuedPriceRequests.set(key, {
        item: {...item, oem_number: key},
        resolvers: [resolve],
      })
    }
    ensureFlushScheduled()
  })

  inFlightPriceRequests.set(key, pendingPromise)
  return pendingPromise
}

export const getHostPartPrice = async (
  item: Ri2PartPriceRequestItem,
): Promise<number | null | undefined> => {
  const data = await getHostPartPriceAndStockStatus(item)
  return data.price
}

export const getHostPartPrices = async (
  items: Ri2PartPriceRequestItem[],
): Promise<Ri2PartPricesMap> => {
  const entries = await Promise.all(
    items.map(async (item) => {
      const key = normalizePriceKey(item.oem_number)
      const partData = await getHostPartPriceAndStockStatus(item)
      return [key, partData] as const
    }),
  )

  return Object.fromEntries(entries)
}
